import { Stack } from 'component-library';
import { cn } from 'component-library/utils';
import { scaleTime } from 'd3-scale';
import React, { forwardRef, HTMLAttributes, useId, useMemo } from 'react';
import { CartesianViewBox } from 'recharts/types/util/types';
import {
  Label,
  LabelProps,
  Legend,
  LegendProps,
  ReferenceLine,
  ResponsiveContainer,
  ResponsiveContainerProps,
  Tooltip,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts-overriden';
import { Payload } from 'recharts-overriden/types/component/DefaultTooltipContent';

import { SHARED_CHART_COLORS } from '@/components/Charts/constants';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';

export type ChartConfig<Key extends string = string> = {
  [k in Key]: {
    label?: React.ReactNode;
  } & {
    /**
     * Color of the indicator used in the tooltip and legend.
     */
    color: string;
    /**
     * Type of the indicator used in the tooltip and legend.
     * @default 'dot'
     */
    icon?: 'dot' | 'line' | 'dashed' | (React.ReactNode & Record<never, never>);
    /**
     * Whether to show this item in the tooltip.
     * @default true
     */
    tooltip?: boolean;
    /**
     * Whether to show this item in the legend.
     * @default true
     */
    legend?: boolean;
  };
};

type ChartData = Record<string, unknown>[];

type ChartContextProps = {
  config: ChartConfig;
  availableAttributes: string[];
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

export type ChartContainerProps = React.ComponentProps<'div'> & {
  config: ChartConfig;
  data: ChartData;
  children: ResponsiveContainerProps['children'];
  /** @default 400 */
  height?: ResponsiveContainerProps['height'];
  width?: ResponsiveContainerProps['width'];
  aspect?: ResponsiveContainerProps['aspect'];
};

const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(
  (
    { id, config: configProp, data: chartData, className, height = 400, width, aspect, children, ...delegated },
    ref,
  ) => {
    const uniqueId = useId();
    const chartId = `chart-${id ?? uniqueId.replaceAll(/:/g, '')}`;

    const chartConfig = Object.entries(configProp).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          ...value,
          icon: value.icon ?? 'dot',
          tooltip: value.tooltip ?? true,
          legend: value.legend ?? true,
        },
      }),
      {} satisfies ChartConfig,
    );

    const availableAttributes = useMemo(() => {
      const attributes = new Set<string>();

      Object.keys(chartConfig).forEach((attribute) => {
        chartData.forEach((item) => {
          // eslint-disable-next-line security/detect-object-injection
          if (!isNullish(item[attribute])) {
            attributes.add(attribute);
          }
        });
      });

      return [...attributes];
    }, [chartConfig, chartData]);

    return (
      <ChartContext.Provider value={{ config: chartConfig, availableAttributes }}>
        <div ref={ref} data-chart={chartId} className={cn('w-full', className)} {...delegated}>
          <ChartStyles chartId={chartId} chartConfig={chartConfig} />
          <ResponsiveContainer height={height} width={width} aspect={aspect}>
            {children}
          </ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    );
  },
);
ChartContainer.displayName = 'ChartContainer';

const Y_AXIS_WIDTH_PX = 50;

type ChartStylesProps = {
  chartId: string;
  chartConfig: ChartConfig;
};

const ChartStyles = ({ chartId, chartConfig }: ChartStylesProps) => {
  const colorConfig = Object.entries(chartConfig).filter(([, c]) => c.color);

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
[data-chart=${chartId}] {
  --y-axis-width-px: ${Y_AXIS_WIDTH_PX}; /* keep unitless */
${colorConfig
  .map(([key, itemConfig]) => {
    const { color } = itemConfig;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join('\n')}
}
`,
      }}
    />
  );
};

function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const payloadPayload =
    'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config
    ? // eslint-disable-next-line security/detect-object-injection
      config[configLabelKey]
    : config[key as keyof typeof config];
}

const Indicator = ({ icon = 'dot', color }: Pick<ChartConfig[string], 'color' | 'icon'>) => {
  const style = { '--indicator-fill': color } as React.CSSProperties;

  if (icon === 'dot') {
    return <div className='mx-[1px] aspect-square w-2 shrink-0 rounded-full bg-[--indicator-fill]' style={style} />;
  }

  if (icon === 'line') {
    return <div className='h-0.5 w-3 shrink-0 rounded bg-[--indicator-fill]' style={style} />;
  }

  if (icon === 'dashed') {
    return <div className='w-3 shrink-0 border-t-2 border-dashed border-[--indicator-fill]' style={style} />;
  }

  return <>{icon ?? null}</>;
};

const ChartTooltip = Tooltip;
ChartTooltip.displayName = Tooltip.displayName;
ChartTooltip.defaultProps = {
  ...Tooltip.defaultProps,
  // @ts-expect-error
  cursor: { stroke: colorPalette.divider },
  position: { y: 0 },
  allowEscapeViewBox: { x: false, y: true },
  isAnimationActive: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  animationDuration: 150,
};

type PayloadItem = Payload<string | number | (string | number)[], string | number>;

export type ChartTooltipContentProps = React.ComponentProps<typeof Tooltip> & React.ComponentProps<'div'>;

export const ChartTooltipContent = forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  ({ active, payload, className, label, labelFormatter, formatter }, ref) => {
    const { config } = useChart();

    const formattedLabel = React.useMemo(() => {
      if (!payload?.length) {
        return null;
      }

      if (labelFormatter) {
        return labelFormatter(label, payload);
      }

      return label.toString?.() ?? label;
    }, [label, labelFormatter, payload]);

    if (!active || !payload?.length) return null;

    return (
      <Stack
        ref={ref}
        direction='col'
        spacing={1.5}
        className={cn('rounded-md bg-white-100 px-2 py-1 elevation-1', className)}
      >
        {formattedLabel && (
          <span className='typography-body2 mt-1 inline-block text-primary-100'>{formattedLabel}</span>
        )}
        <Stack spacing={1}>
          {payload.map((item, index) => {
            if (isNullish(item.value) || !item.name) {
              return null;
            }
            const key = `${item.name || item.dataKey || 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);

            if (!itemConfig?.tooltip) {
              return null;
            }

            const formattedValue = formatter?.(item.value, item.name, item, index, item.payload) ?? item.value;
            return (
              <Stack key={item.dataKey} direction='row' centerMain className='justify-between' spacing={4}>
                <Stack direction='row' spacing={2} centerMain>
                  <Indicator color={itemConfig.color} icon={itemConfig?.icon} />
                  <span className='typography-body2 text-text-secondary'>{itemConfig?.label ?? item.name}</span>
                </Stack>
                <span className='typography-body2Semibold mt-px inline-block text-primary-100'>{formattedValue}</span>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

export const ChartTooltipTextPrimary = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...delegated }, ref) => {
    return <p ref={ref} className={cn('typography-body2Semibold text-text-secondary', className)} {...delegated} />;
  },
);
ChartTooltipTextPrimary.displayName = 'ChartTooltipTextPrimary';

export const ChartTooltipTextSecondary = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...delegated }, ref) => {
    return <p ref={ref} className={cn('typography-body2 text-text-secondary', className)} {...delegated} />;
  },
);
ChartTooltipTextSecondary.displayName = 'ChartTooltipTextSecondary';

const ChartLegend = Legend;
ChartLegend.displayName = Legend.displayName;
ChartLegend.defaultProps = {
  ...Legend.defaultProps,
  verticalAlign: 'bottom',
  align: 'right',
  // @ts-expect-error
  wrapperStyle: {
    paddingLeft: 'calc(calc(var(--y-axis-width-px) * 1px) - 4px)',
    paddingTop: '32px',
  },
};

export type ChartLegendContentProps = Pick<LegendProps, 'payload'> & {
  onAttributeHover?: (dataKey: PayloadItem['dataKey'] | null) => void;
};

const ChartLegendContent = ({ payload, onAttributeHover }: ChartLegendContentProps) => {
  const { config, availableAttributes } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <Stack
      direction='row'
      centerMain
      className={cn('max-w-fit flex-wrap rounded-lg bg-bg-light-grey p-[3px]')}
      onMouseLeave={() => onAttributeHover?.(null)}
    >
      {(payload as PayloadItem[]).map((item) => {
        const key = `${item.dataKey || 'value'}`;

        if (!availableAttributes.includes(key)) {
          return null;
        }

        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        if (!itemConfig?.legend) {
          return null;
        }

        return (
          <Stack
            key={item.dataKey}
            direction='row'
            spacing={2}
            centerMain
            className={cn('rounded-md py-1.5 pl-2 pr-[10px]', onAttributeHover && 'hover:bg-neutral-black-8')}
            onMouseOver={() => onAttributeHover?.(key)}
            onMouseLeave={() => onAttributeHover?.(null)}
          >
            <Indicator color={itemConfig.color} icon={itemConfig?.icon} />
            <span className='typography-caption text-text-secondary'>{itemConfig?.label ?? item.name}</span>
          </Stack>
        );
      })}
    </Stack>
  );
};

const ChartLabel = ({ className, children }: { className?: string; children?: React.ReactNode }) => {
  return (
    <span
      /* @ts-expect-error the xmlns attribute is required when embedding HTML inside SVG via foreignObject */
      xmlns='http://www.w3.org/1999/xhtml'
      className={cn('typography-caption rounded bg-[rgba(255,_255,_255,_0.8)] p-1 text-neutral-black-60', className)}
    >
      {children}
    </span>
  );
};

const axisDefaults = {
  tick: {
    fill: 'rgba(0, 0, 0, 0.6)', // text-secondary
    fontSize: 12,
  },
  stroke: SHARED_CHART_COLORS.axis.stroke,
  tickLine: false,
  type: 'number',
  tickMargin: 16,
} satisfies XAxisProps;

type TimeseriesItem = ConstructorParameters<typeof Date>[0];

const ChartXAxis = XAxis;
ChartXAxis.displayName = XAxis.displayName;
ChartXAxis.defaultProps = {
  ...XAxis.defaultProps,
  ...axisDefaults,
  type: 'number',
};

export const useTimeseriesXAxisProps = (chartData: ChartData, dataKey: XAxisProps['dataKey'] = 'date') => {
  const timeseries = chartData.map((item) => new Date(item[dataKey as string] as TimeseriesItem).valueOf());

  const domain = [Math.min(...timeseries), Math.max(...timeseries)];

  const scale = scaleTime().domain(domain).nice();

  return {
    scale,
    domain,
    dataKey,
    tickFormatter: printYear,
    ticks: [
      domain[0] as number,
      ...scale
        .ticks(Math.min(timeseries.length, 5))
        .map((date) => date.valueOf())
        .slice(1, -1),
      domain[1] as number,
    ],
  };
};

const ChartYAxis = YAxis;
ChartYAxis.displayName = YAxis.displayName;
ChartYAxis.defaultProps = {
  ...YAxis.defaultProps,
  ...axisDefaults,
  tick: { ...axisDefaults.tick, dy: -5 },
  padding: { top: 50 },
  width: Y_AXIS_WIDTH_PX,
  scale: 'sequential',
} satisfies YAxisProps;

const ChartYAxisLabel = ({ className, ...delegated }: LabelProps) => (
  <Label
    className={cn('typography-caption text-text-secondary', className)}
    position={{ x: Y_AXIS_WIDTH_PX - 20, y: 10 }}
    {...delegated}
  />
);
ChartYAxisLabel.displayName = Label.displayName;

const ChartBenchmark = ReferenceLine;
ChartBenchmark.displayName = ReferenceLine.displayName;
ChartBenchmark.defaultProps = {
  ...ReferenceLine.defaultProps,
  // @ts-expect-error types for defaultProps incorrectly excludes most props from ReferenceLineProps
  strokeDasharray: '3 3',
  stroke: '#0000004D',
} satisfies typeof ReferenceLine.defaultProps;

const ChartBenchmarkHorizontalLabel = ({
  className,
  viewBox,
  children,
}: {
  children?: React.ReactNode;
  viewBox: CartesianViewBox;
  className?: string;
}) => {
  // @ts-expect-error values are always of type number
  const x = viewBox.x - getPercentValue(1, viewBox.width);

  // @ts-expect-error values are always of type number
  const y = viewBox.y - 28;

  return (
    <foreignObject className='overflow-visible text-right' width={viewBox?.width} height={viewBox?.height} x={x} y={y}>
      <span
        /* @ts-expect-error the xmlns attribute is required when embedding HTML inside SVG via foreignObject */
        xmlns='http://www.w3.org/1999/xhtml'
        className={cn('typography-caption rounded bg-[rgba(255,_255,_255,_0.8)] p-1 text-neutral-black-60', className)}
      >
        {children}
      </span>
    </foreignObject>
  );
};
ChartBenchmarkHorizontalLabel.displayName = Label.displayName;

const getPercentValue = (percentage: number | string, total: number) => {
  // @ts-expect-error parseFloat works with inputs of type number
  const percentageNumeric = parseFloat(percentage);

  return (percentageNumeric / 100) * total;
};

export * from 'recharts-overriden';

export const Chart = {
  Container: ChartContainer,
  Label: ChartLabel,
  XAxis: ChartXAxis,
  YAxis: ChartYAxis,
  YAxisLabel: ChartYAxisLabel,
  Tooltip: ChartTooltip,
  TooltipContent: ChartTooltipContent,
  Legend: ChartLegend,
  LegendContent: ChartLegendContent,
  Benchmark: ChartBenchmark,
  BenchmarkHorizontalLabel: ChartBenchmarkHorizontalLabel,
};
